import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Citation from './components/Citation';
import DailyQuote from './components/DailyQuote';
import LoadingSpinner from './components/LoadingSpinner';
import AdminPanel from './components/AdminPanel';
import ProtectedRoute from './components/ProtectedRoute';
import HistoryView from './components/HistoryView';
import DebugInfo from './components/DebugInfo';
import { fetchRandomMessage, fetchDailyMessage } from './services/api';
import { usePageTracking } from './hooks/usePageTracking';
import { Message, DailyMessage } from './types';
import config, { logConfig } from './config';

const MainApp: React.FC = () => {
  const [dailyMessage, setDailyMessage] = useState<DailyMessage | null>(null);
  const [randomMessage, setRandomMessage] = useState<Message | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showDaily, setShowDaily] = useState<boolean>(true);
  const [showHistory, setShowHistory] = useState<boolean>(false);

  // Activer le tracking des pages
  usePageTracking();

  // Initialiser la configuration au démarrage
  useEffect(() => {
    logConfig();
  }, []);

  const loadDailyMessage = async () => {
    setLoading(true);
    try {
      const daily = await fetchDailyMessage();
      setDailyMessage(daily);
    } catch (error) {
      console.error('Erreur lors du chargement de la citation du jour:', error);
    }
    setLoading(false);
  };

  const refreshRandomMessage = async () => {
    setLoading(true);
    try {
      const random = await fetchRandomMessage();
      setRandomMessage(random);
      setShowDaily(false);
      setShowHistory(false);
    } catch (error) {
      console.error('Erreur lors du chargement du message aléatoire:', error);
    }
    setLoading(false);
  };

  const showDailyMessage = () => {
    setShowDaily(true);
    setShowHistory(false);
  };

  const showHistoryPage = () => {
    setShowHistory(true);
    setShowDaily(false);
  };

  const goBackToMain = () => {
    setShowHistory(false);
    setShowDaily(true);
  };

  useEffect(() => {
    loadDailyMessage();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="relative">
      {showHistory ? (
        <div className="min-h-screen bg-gray-50 p-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-6">
              <button
                onClick={goBackToMain}
                className="bg-gradient-to-r from-gray-500 to-gray-600 text-white font-bold py-2 px-4 rounded-full hover:from-gray-600 hover:to-gray-700 transition duration-300 shadow-lg hover:shadow-xl"
              >
                ← Retour aux citations
              </button>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <HistoryView />
            </div>
          </div>
        </div>
      ) : showDaily && dailyMessage ? (
        <DailyQuote dailyMessage={dailyMessage} />
      ) : randomMessage ? (
        <Citation message={randomMessage} />
      ) : null}
      
      {!showHistory && (
        <div className="fixed bottom-8 right-8 flex flex-col gap-3 z-50">
          {!showDaily && (
            <button
              onClick={showDailyMessage}
              className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold py-3 px-6 rounded-full hover:from-blue-600 hover:to-indigo-600 transition duration-300 shadow-lg hover:shadow-xl"
            >
              Citation du jour 📅
            </button>
          )}
          <button
            onClick={showHistoryPage}
            className="bg-gradient-to-r from-green-500 to-teal-500 text-white font-bold py-3 px-6 rounded-full hover:from-green-600 hover:to-teal-600 transition duration-300 shadow-lg hover:shadow-xl"
          >
            Historique 📖
          </button>
          <button
            onClick={refreshRandomMessage}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-3 px-6 rounded-full hover:from-purple-600 hover:to-pink-600 transition duration-300 shadow-lg hover:shadow-xl"
          >
            Message aléatoire ✨
          </button>
        </div>
      )}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainApp />} />
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute>
              <AdminPanel />
            </ProtectedRoute>
          } 
        />
      </Routes>
      <DebugInfo />
    </Router>
  );
};

export default App;